import React, { useState, useEffect } from 'react';
import { 
  Bookmark, 
  Trash2, 
  Route, 
} from 'lucide-react';
import { Spot, Language, UserLocation } from '../types/spot';
import { formatDistance, formatCompactTransitBadge } from '../services/geolocation';
import { openKakaoMap } from '../services/mapLinks';
import { useSpotImage } from '../hooks/useSpotImage';
import { getCategoryPlaceholder } from '../services/imageResolver';

interface FavoritesDrawerProps {
  savedSpots: Spot[];
  language: Language;
  userLocation: UserLocation;
  onRemoveSaved: (spotId: string) => void;
  onClearAll: () => void;
  onSelectSpot: (spot: Spot) => void;
  onOpenRoutePlanner: () => void;
}

const SavedSpotCardItem: React.FC<{
  spot: Spot;
  language: Language;
  userLocation: UserLocation;
  onRemoveSaved: (id: string) => void;
  onSelectSpot: (spot: Spot) => void;
}> = ({ spot, language, userLocation, onRemoveSaved, onSelectSpot }) => {
  const { imageUrl } = useSpotImage(spot);
  const [imgOverride, setImgOverride] = useState<string | null>(null);

  const title = language === 'de' ? spot.nameDe : language === 'kr' ? spot.nameKr : spot.name;
  const currentImage = imgOverride || imageUrl;

  return (
    <div
      onClick={() => onSelectSpot(spot)}
      className="saved-card"
    >
      {/* Thumbnail */}
      <div className="saved-card__thumb">
        <img
          src={currentImage}
          alt={title}
          referrerPolicy="no-referrer"
          onError={() => setImgOverride(getCategoryPlaceholder(spot.category))}
        />
        {spot.calculatedDistance !== undefined && (
          <div
            className="saved-card__distance"
            title={`${formatDistance(spot.calculatedDistance)} (Luftlinie)`}
          >
            {formatCompactTransitBadge(spot.calculatedDistance, language)}
          </div>
        )}
      </div>

      {/* Info & Buttons */}
      <div className="saved-card__body">
        <div>
          <div className="saved-card__top">
            <span className="saved-card__hangul">{spot.nameKr}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onRemoveSaved(spot.id); }}
              className="saved-card__remove"
              title={language === 'kr' ? '삭제' : language === 'en' ? 'Remove' : 'Entfernen'}
            >
              <Trash2 style={{ width: 14, height: 14 }} />
            </button>
          </div>

          <h4 className="saved-card__title">{title}</h4>
          <p className="saved-card__meta">{spot.subwayInfo.split('(')[0]}</p>
        </div>

        {/* Map Launcher */}
        <div className="saved-card__btns">
          <button
            onClick={(e) => { e.stopPropagation(); openKakaoMap(spot, false, userLocation); }}
            className="btn btn--kakao btn--sm btn--full"
            style={{ fontWeight: 800, padding: '6px 10px', fontSize: '11.5px', justifyContent: 'center' }}
          >
            {language === 'kr' ? '카카오맵에서 열기' : language === 'en' ? 'Open in KakaoMap' : 'In KakaoMap öffnen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const FavoritesDrawer: React.FC<FavoritesDrawerProps> = ({
  savedSpots,
  language,
  userLocation,
  onRemoveSaved,
  onClearAll,
  onSelectSpot,
  onOpenRoutePlanner,
}) => {
  const t = {
    de: {
      headerTitle: `Gemerkte Orte (${savedSpots.length})`,
      headerSub: 'Offline auf deinem Gerät gespeichert',
      planRoute: 'Tagesroute planen',
      clearAll: 'Leeren',
      clearAllTooltip: 'Alle Markierungen löschen',
      emptyTitle: 'Noch keine Spots gemerkt',
      emptyText: 'Klicke bei beliebigen Spots auf das Lesezeichen, um sie hier für deine Route zu sammeln.',
    },
    en: {
      headerTitle: `Saved Places (${savedSpots.length})`,
      headerSub: 'Saved offline on your device',
      planRoute: 'Plan Day Route',
      clearAll: 'Clear All',
      clearAllTooltip: 'Clear all saved spots',
      emptyTitle: 'No spots saved yet',
      emptyText: 'Click the bookmark icon on any spot to save it here for your custom route.',
    },
    kr: {
      headerTitle: `저장된 장소 (${savedSpots.length})`,
      headerSub: '기기에 오프라인으로 안전하게 저장됨',
      planRoute: '당일 코스 계획',
      clearAll: '전체 삭제',
      clearAllTooltip: '모든 북마크 삭제',
      emptyTitle: '저장된 장소가 없습니다',
      emptyText: '명소의 북마크 아이콘을 클릭하여 나만의 여행 코스에 추가해보세요.',
    },
  }[language];

  return (
    <div className="flex flex-col gap-4">
      {/* Top Action Bar */}
      <div className="favorites-header">
        <div className="favorites-header__info">
          <div className="favorites-header__icon">
            <Bookmark style={{ width: 20, height: 20, fill: 'currentColor' }} />
          </div>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              {t.headerTitle}
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {t.headerSub}
            </p>
          </div>
        </div>

        {savedSpots.length > 0 && (
          <div className="favorites-header__actions">
            <button onClick={onOpenRoutePlanner} className="btn btn--primary btn--sm">
              <Route style={{ width: 16, height: 16 }} />
              <span>{t.planRoute}</span>
            </button>
            <button
              onClick={onClearAll}
              className="btn btn--ghost btn--sm btn--danger"
              title={t.clearAllTooltip}
            >
              <Trash2 style={{ width: 16, height: 16 }} />
              <span>{t.clearAll}</span>
            </button>
          </div>
        )}
      </div>

      {/* Empty State */}
      {savedSpots.length === 0 ? (
        <div className="empty-state">
          <Bookmark className="empty-state__icon" style={{ width: 40, height: 40 }} />
          <h3 className="empty-state__title" style={{ fontSize: 15 }}>
            {t.emptyTitle}
          </h3>
          <p className="empty-state__text">
            {t.emptyText}
          </p>
        </div>
      ) : (
        /* Saved Spots Grid */
        <div className="saved-grid">
          {savedSpots.map((spot) => (
            <SavedSpotCardItem
              key={spot.id}
              spot={spot}
              language={language}
              userLocation={userLocation}
              onRemoveSaved={onRemoveSaved}
              onSelectSpot={onSelectSpot}
            />
          ))}
        </div>
      )}
    </div>
  );
};
